import classnames from 'classnames';
import { type ReactNode, memo } from 'react';
import '../../../../css/admin.less';
import '../../../../css/modal.less';
import { TAB_TYPES } from '../../constants/tab';
import translations from '../../translations';
import type { Tab } from '../../types/tab';
import ApiTool from '../ApiTool';
import Translations from '../Translations';
import CustomPageComponents from '../admin/customPageComponents/CustomPageComponents';
import { CustomPageComponentsProvider } from '../admin/customPageComponents/CustomPageComponentsProvider';
import Environment from '../admin/environments/Environment';
import Environments from '../admin/environments/Environments';
import { EnvironmentsProvider } from '../admin/environments/EnvironmentsProvider';
import IdentityProviders from '../admin/identityproviders/IdentityProviders';
import PageList from '../admin/pages/PageList';
import Values from '../admin/values/Values';
import { ValuesProvider } from '../admin/values/ValuesProvider';
import AssetTab from '../assets/AssetTab';
import AuditLogs from '../auditLogs/AuditLogs';
import InsightsDashboard from '../developerDashboard/InsightsDashboard';
import Features from '../featureFlags/Features';
import Home from '../flow/homePage/Home';
import ErrorBoundary from '../generic/ErrorBoundary';
import FlowTab from '../graph/FlowTab';
import MacroEditor from '../macro-editor/MacroEditor';
import InitialOrgPage from '../organization/InitialOrgPage';
import PageEditor from '../page-editor/components/gui/PageEditor';
import PlayerEditor from '../player-editor/PlayerEditor';
import ServiceController from '../services/ServiceController';
import { ServicesProvider } from '../services/contextProviders/ServicesProvider';
import TenantPageSwitcher from '../tenant/TenantPageSwitcher';
import Themes from '../theme/Themes';
import Types from '../types/Types';
import { TypesProvider } from '../types/TypesProvider';
import UserSettings from '../userSettings/UserSettings';
import type { UserTenantResponseAPI } from '../../types/user';

type AdminProps = {
    isActive: boolean;
    tab: Tab;
    tenant: UserTenantResponseAPI;
    isTenantSelectorOpen: boolean;
};

const TabContent = ({ isActive, tab, isTenantSelectorOpen, tenant }: AdminProps) => {
    const { tenantSettings } = tenant;
    const environmentsIsOn = tenantSettings?.environments ?? false;

    let content: ReactNode = null;

    switch (tab.type) {
        case TAB_TYPES.flow: {
            content = (
                <ErrorBoundary>
                    <FlowTab
                        tab={tab}
                        isActive={isActive}
                        isTenantSelectorOpen={isTenantSelectorOpen}
                    />
                </ErrorBoundary>
            );
            break;
        }
        case TAB_TYPES.newPage:
        case TAB_TYPES.page: {
            content = (
                <ErrorBoundary>
                    <PageEditor tab={tab} isActive={isActive} />
                </ErrorBoundary>
            );
            break;
        }
        case TAB_TYPES.auditing: {
            content = (
                <ErrorBoundary>
                    <AuditLogs />
                </ErrorBoundary>
            );
            break;
        }
        case TAB_TYPES.tenant: {
            content = (
                <ErrorBoundary>
                    <TenantPageSwitcher tenant={tenant} />
                </ErrorBoundary>
            );
            break;
        }
        case TAB_TYPES.flows: {
            content = (
                <ErrorBoundary>
                    <Home isEditorActive={isActive} />
                </ErrorBoundary>
            );
            break;
        }
        case TAB_TYPES.services: {
            content = (
                <ErrorBoundary>
                    <ServicesProvider>
                        <ServiceController />
                    </ServicesProvider>
                </ErrorBoundary>
            );
            break;
        }
        case TAB_TYPES.types: {
            content = (
                <ErrorBoundary>
                    <TypesProvider>
                        <Types />
                    </TypesProvider>
                </ErrorBoundary>
            );
            break;
        }
        case TAB_TYPES.values: {
            content = (
                <ValuesProvider>
                    <Values />
                </ValuesProvider>
            );
            break;
        }
        case TAB_TYPES.identityProviders: {
            content = (
                <ErrorBoundary>
                    <IdentityProviders />
                </ErrorBoundary>
            );
            break;
        }
        case TAB_TYPES.environments: {
            content = (
                <ErrorBoundary>
                    <EnvironmentsProvider>
                        <Environments isActive={isActive} />
                    </EnvironmentsProvider>
                </ErrorBoundary>
            );
            break;
        }
        case TAB_TYPES.environment: {
            if (environmentsIsOn && tab.elementId) {
                content = (
                    <ErrorBoundary>
                        <EnvironmentsProvider>
                            <Environment
                                tabKey={tab.key}
                                environmentId={tab.elementId}
                                isActive={isActive}
                            />
                        </EnvironmentsProvider>
                    </ErrorBoundary>
                );
            } else {
                content = <span>{translations.ENVIRONMENT_not_on}</span>;
            }
            break;
        }
        case TAB_TYPES.pages: {
            content = (
                <ErrorBoundary>
                    <PageList isActive={isActive} />
                </ErrorBoundary>
            );
            break;
        }
        case TAB_TYPES.assets: {
            content = (
                <ErrorBoundary>
                    <AssetTab />
                </ErrorBoundary>
            );
            break;
        }
        case TAB_TYPES.themes: {
            content = (
                <ErrorBoundary>
                    <Themes key="themes" />
                </ErrorBoundary>
            );
            break;
        }
        case TAB_TYPES.players: {
            content = (
                <ErrorBoundary>
                    <PlayerEditor key="players" isActive={isActive} />
                </ErrorBoundary>
            );
            break;
        }
        case TAB_TYPES.api: {
            content = (
                <ErrorBoundary>
                    <ApiTool key="api-tool" isActive={isActive} />
                </ErrorBoundary>
            );
            break;
        }
        case TAB_TYPES.macros: {
            content = (
                <ErrorBoundary>
                    <MacroEditor key="macro-editor" />
                </ErrorBoundary>
            );
            break;
        }
        case TAB_TYPES.translations: {
            content = (
                <ErrorBoundary>
                    <Translations key="translations" isActive={isActive} />
                </ErrorBoundary>
            );
            break;
        }
        case TAB_TYPES.dashboard: {
            content = (
                <ErrorBoundary>
                    <InsightsDashboard />
                </ErrorBoundary>
            );
            break;
        }
        case TAB_TYPES.organization: {
            content = (
                <ErrorBoundary>
                    <InitialOrgPage />
                </ErrorBoundary>
            );
            break;
        }
        case TAB_TYPES.userSettings: {
            content = <UserSettings tenant={tenant} />;
            break;
        }
        case TAB_TYPES.features: {
            content = <Features />;
            break;
        }
        case TAB_TYPES.components: {
            content = (
                <ErrorBoundary>
                    <CustomPageComponentsProvider>
                        <CustomPageComponents />
                    </CustomPageComponentsProvider>
                </ErrorBoundary>
            );
            break;
        }
        default: {
            content = null;
            break;
        }
    }

    const classes = classnames('admin', {
        active: isActive,
    });

    return (
        <div className={classes}>
            <span className="admin-transition">{content}</span>
        </div>
    );
};

export default memo(TabContent);
